import { useNavigate, useParams, useLocation } from "react-router-dom";
import config from "../config";
import { DetailsActionContext } from "@beeldit/core/actions/details/DetailsActionContext";
import { useContext, useEffect, useState } from "react";
import useDetailsAction from "@beeldit/core/actions/details/useDetailsAction";
import { AuthContext } from "@beeldit/user-and-access/auth/AuthContext";
import { Button, Card, Nav, Tab } from "react-bootstrap";
import {
  Calendar,
  HandCoins,
  LineSegments,
  Money,
  Pen,
  User,
} from "@phosphor-icons/react";
import { t } from "i18next";
import CustomersTreatmentsPage from "@main/customers-treatments/pages/CustomersTreatmentsPage";
import { Session } from "inspector";
import SessionsPage from "@main/sessions/pages/SessionsPage";
import { Debt } from "@main/debts/models/Debt";
import DebtsPage from "@main/debts/pages/DebtsPage";
import ChargesPage from "@main/charges/pages/ChargesPage";
import useCustomerHistoryDetailsAction from "@main/customer-histories/actions/details/useDetailsByCustomerAction";
import { DetailsByCustomerActionProvider } from "@main/customer-histories/actions/details/DetailsByCustomerActionContext";
import customerHistoryConfig from "@main/customer-histories/config";
import { Caduceus } from "@phosphor-icons/react/dist/ssr";
import { CustomerNote } from "@main/customer-notes/models/CustomerNote";
import CustomerNotesPage from "@main/customer-notes/pages/CustomerNotesPage";

function CustomerPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  // Roles que pueden ver las pestañas administrativas (datos, cobros, deudas)
  const adminTabsRoles = ["admin", "superadmin"];

  // Función para verificar si el usuario tiene acceso a las pestañas administrativas
  const hasAdminAccess = () => {
    return user && adminTabsRoles.includes(user.role);
  };

  let { customerId } = useParams();

  if (!customerId) {
    throw new Error("Página no encontrada");
  }

  const { getFunction, actionTemplate, onSubmitFunction } = useDetailsAction(
    config,
    parseInt(customerId)
  );

  const {
    getFunction: customerHistoryGetFunction,
    actionTemplate: customerHistoryActionTemplate,
    onSubmitFunction: customerHistoryOnSubmitFunction,
  } = useCustomerHistoryDetailsAction(
    customerHistoryConfig,
    parseInt(customerId)
  );

  useEffect(() => {
    getFunction(customerId);
    customerHistoryGetFunction(customerId);
  }, [customerId]);

  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam) {
      setActiveTab(tabParam);
      onSelect(tabParam);
    }
  }, [location.search]);

  // se prescindió de estados de carga de pestañas no usados

  const onSelect = (key: string) => {
    // espacio reservado para carga perezosa de pestañas si se desea
  };

  // Determinar la pestaña por defecto según el rol del usuario
  const getDefaultTab = () => {
    return hasAdminAccess() ? "customer" : "treatments";
  };

  const [activeTab, setActiveTab] = useState(getDefaultTab());
  const handleSelect = (eventKey: string | null) => {
    if (eventKey) {
      setActiveTab(eventKey);
    }
  };
  const goToCustomers = () => {
    navigate("/panel/1/1/customers");
  };

  return (
    <div className="">
      <Tab.Container
        id="left-tabs-example"
        activeKey={activeTab}
        onSelect={(key: any) => {
          onSelect(key);
          if (key) setActiveTab(key);
        }}
      >
        <Nav variant="tabs" className="nav-tabs" onSelect={handleSelect}>
          {/* Pestaña de datos del paciente - solo para administradores */}
          {hasAdminAccess() && (
            <Nav.Item>
              <Nav.Link
                eventKey="customer"
                active={activeTab === "customer"}
                className={activeTab === "customer" ? "active-tab-class" : ""}
              >
                <User size={24} />
                <span className="d-none d-lg-inline">{t("customer")}</span>
              </Nav.Link>
            </Nav.Item>
          )}
          {/* Tab para tratamientos */}
          <Nav.Item>
            <Nav.Link
              eventKey="treatments"
              active={activeTab === "treatments"}
              className={activeTab === "treatments" ? "active-tab-class" : ""}
            >
              <Pen size={24} />
              <span className="d-none d-lg-inline">{t("treatments")}</span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="sessions"
              active={activeTab === "sessions"}
              className={activeTab === "sessions" ? "active-tab-class" : ""}
            >
              <Calendar size={24} />
              <span className="d-none d-lg-inline">
                {t("uncoming_sessions")}
              </span>
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              eventKey="past-sessions"
              active={activeTab === "past-sessions"}
              className={
                activeTab === "past-sessions" ? "active-tab-class" : ""
              }
            >
              <Calendar size={24} />
              <span className="d-none d-lg-inline">{t("past_sessions")}</span>
            </Nav.Link>
          </Nav.Item>
          {/* Pestaña de deudas - solo para administradores */}
          {hasAdminAccess() && (
            <Nav.Item>
              <Nav.Link
                eventKey="debts"
                active={activeTab === "debts"}
                className={activeTab === "debts" ? "active-tab-class" : ""}
              >
                <Money size={24} />
                <span className="d-none d-lg-inline ms-1">{t("debts")}</span>
              </Nav.Link>
            </Nav.Item>
          )}
          {/* Pestaña de cobros - solo para administradores */}
          {hasAdminAccess() && (
            <Nav.Item>
              <Nav.Link
                eventKey="charges"
                active={activeTab === "charges"}
                className={activeTab === "charges" ? "active-tab-class" : ""}
              >
                <HandCoins size={24} />
                <span className="d-none d-lg-inline ms-1">{t("charges")}</span>
              </Nav.Link>
            </Nav.Item>
          )}
          <Nav.Item>
            <Nav.Link
              eventKey="follow-ups"
              active={activeTab === "follow-ups"}
              className={activeTab === "follow-ups" ? "active-tab-class" : ""}
            >
              <LineSegments size={24} />
              <span className="d-none d-lg-inline ms-1">{t("follow_ups")}</span>
            </Nav.Link>
          </Nav.Item>
        </Nav>
        <Tab.Content>
          {/* Panel de datos del paciente - solo para administradores */}
          {hasAdminAccess() && (
            <Tab.Pane eventKey="customer">
              <Card>
                <Card.Body>
                  <div className="PageEditContainer">{actionTemplate}</div>
                </Card.Body>

                <Card.Footer className="d-flex py-4">
                  <Button
                    variant="primary"
                    size="lg"
                    className="me-2"
                    onClick={() => {
                      goToCustomers();
                    }}
                  >
                    {t("back")}
                  </Button>
                  <Button
                    variant="primary"
                    size="lg"
                    className="ms-auto"
                    onClick={onSubmitFunction}
                  >
                    {t("save_changes")}
                  </Button>
                </Card.Footer>
              </Card>
            </Tab.Pane>
          )}
          <Tab.Pane eventKey="treatments">
            <Card>
              <CustomersTreatmentsPage
                filters={{
                  customer_id: parseInt(customerId),
                }}
                formDefaultValues={{ customer_id: parseInt(customerId) }}
              ></CustomersTreatmentsPage>
            </Card>
          </Tab.Pane>
          <Tab.Pane eventKey="sessions">
            <Card>
              <SessionsPage
                filters={{
                  customer_id: parseInt(customerId),
                  type: "upcoming",
                }}
                formDefaultValues={{ customer_id: parseInt(customerId) }}
              ></SessionsPage>
            </Card>
          </Tab.Pane>
          <Tab.Pane eventKey="past-sessions">
            <Card>
              <SessionsPage
                filters={{ customer_id: parseInt(customerId), type: "past" }}
                formDefaultValues={{ customer_id: parseInt(customerId) }}
              ></SessionsPage>
            </Card>
          </Tab.Pane>
          {/* Panel de deudas - solo para administradores */}
          {hasAdminAccess() && (
            <Tab.Pane eventKey="debts">
              <Card>
                <DebtsPage
                  filters={{ customer_id: parseInt(customerId) }}
                  formDefaultValues={{ customer_id: parseInt(customerId) }}
                  formFilterValues={{
                    customer_treatment_id: {
                      customer_id: parseInt(customerId),
                    },
                  }}
                ></DebtsPage>
              </Card>
            </Tab.Pane>
          )}
          {/* Panel de cobros - solo para administradores */}
          {hasAdminAccess() && (
            <Tab.Pane eventKey="charges">
              <Card>
                <ChargesPage
                  filters={{ customer_id: parseInt(customerId) }}
                  formDefaultValues={{ customer_id: parseInt(customerId) }}
                  formFilterValues={{
                    debt_id: { customer_id: parseInt(customerId) },
                  }}
                ></ChargesPage>
              </Card>
            </Tab.Pane>
          )}
          <Tab.Pane eventKey="follow-ups">
            <Card>
              <Card.Body>
                <div className="PageEditContainer">
                  {customerHistoryActionTemplate}
                </div>

                <Button
                  variant="primary"
                  size="lg"
                  className="ms-auto"
                  onClick={customerHistoryOnSubmitFunction}
                >
                  {t("save_changes")}
                </Button>
                <hr />

                <div className="mt-3">
                  <CustomerNotesPage
                    filters={{
                      customer_id: parseInt(customerId),
                    }}
                    formDefaultValues={{ customer_id: parseInt(customerId) }}
                  ></CustomerNotesPage>
                </div>
              </Card.Body>
            </Card>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
}

export default CustomerPage;
